import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, OnInit, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, Validators } from '@angular/forms';
import { TranslocoDirective } from '@jsverse/transloco';
import { ModuleResolverInputs } from '@modules/forms/models/module-resolver-inputs';
import { SadmClientActionButtonComponent } from '@modules/sadm/components/sadm-client-action-button/sadm-client-action-button.component';
import { SadmEntityDisplay } from '@modules/sadm/models/sadm-entity-display';
import { Store } from '@ngrx/store';
import { SadmClient } from '@pixacare/dtos';
import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';
import { SadmEntityDraft } from '@shared/models/draft/sadm-entity-draft';
import { sadmActions } from '@shared/store/sadm/sadm.actions';
import { TuiButton, TuiDialogContext } from '@taiga-ui/core';
import { TuiFade } from '@taiga-ui/kit';
import { injectContext } from '@taiga-ui/polymorpheus';
import { defer, first, map, Observable, of } from 'rxjs';
import {
  selectIsPatientSadmStateLoaded,
  selectPatientSadmEntities,
  selectSadmClients,
} from 'src/app/shared/store/sadm/sadm.selectors';
import { SadmEntityService } from '../../services/sadm-entity.service';
import { SadmEntitySelectListComponent } from '../sadm-entity-select-list/sadm-entity-select-list.component';
import { SadmEntityPickerInput } from './sadm-entity-picker-input';
import { SadmEntityPickerOutput } from './sadm-entity-picker-output';

@Component({
  selector: 'pxc-sadm-entity-picker',
  templateUrl: './sadm-entity-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TranslocoDirective,
    TuiButton,
    TuiFade,
    SadmClientActionButtonComponent,
    SadmEntitySelectListComponent,
    EmptyStateComponent,
  ],
  providers: [SadmEntityService],
  host: { class: 'max-h-[75vh] flex flex-col' },
})
export class SadmEntityPickerComponent implements OnInit {
  private readonly context = injectContext<TuiDialogContext<SadmEntityPickerOutput, SadmEntityPickerInput>>({
    optional: true,
  });

  readonly clientCode = input<string>(this.context?.data.clientCode);
  readonly patientId = input<number>(this.context?.data.patientId);
  readonly sadmClientId = input<number | undefined>(this.context?.data.sadmClientId);
  readonly draft = input<SadmEntityDraft | undefined>(this.context?.data.sadmEntityPlaceholder);
  readonly autoselection = input<boolean>(this.context?.data.autoselection ?? false);
  readonly formInputs = input<ModuleResolverInputs>(this.context?.data.formInputs ?? {});
  readonly isDialog = input<boolean>(!!this.context?.data);

  readonly selectionChange = output<SadmEntityPickerOutput>();
  readonly entityCreated = output<SadmEntityDraft>();

  private readonly store = inject(Store);
  private readonly sadmEntityService = inject(SadmEntityService);
  private readonly destroyRef = inject(DestroyRef);

  readonly DRAFT_PLACEHOLDER_ID = -1;

  private readonly isPickedSadmClient = (sadmClientId: number): boolean =>
    !this.sadmClientId() || sadmClientId === this.sadmClientId();

  readonly sadms$: Observable<SadmClient[]> = this.store
    .select(selectSadmClients)
    .pipe(map((sadmClients) => sadmClients.filter((sadmClient) => this.isPickedSadmClient(sadmClient.id))));

  // Only entities the user can write to: picking one attaches a sequence to it.
  // Deferred so the inputs are read once set, not at construction.
  readonly sadmEntities$: Observable<SadmEntityDisplay[]> = defer(() =>
    this.patientId()
      ? this.store.select(selectPatientSadmEntities(this.clientCode(), this.patientId(), false))
      : of<SadmEntityDisplay[]>([]),
  ).pipe(
    map((entities) =>
      entities
        .filter((entity) => entity.canWrite && this.isPickedSadmClient(entity.sadmClientId))
        .sort((entityA, entityB) => entityB.createdOn.getTime() - entityA.createdOn.getTime()),
    ),
  );

  isDataLoaded$: Observable<boolean>;

  private readonly autoselectedSadmEntity$: Observable<SadmEntityDisplay> = this.sadmEntities$.pipe(
    map(
      (entities) =>
        [...entities].sort((entityA, entityB) => entityA.createdOn?.getTime() - entityB.createdOn?.getTime())[0],
    ),
  );

  readonly displayEntities$: Observable<SadmEntityDisplay[]> = this.sadmEntities$.pipe(
    map((entities) => (entities.length > 0 ? entities : this.draftEntities())),
  );

  readonly selectedSadmIdControl = new FormControl<number>(undefined, [Validators.required]);

  private draftEntities(): SadmEntityDisplay[] {
    const draft = this.draft();
    return draft ? [{ id: this.DRAFT_PLACEHOLDER_ID, name: draft.name } as SadmEntityDisplay] : [];
  }

  ngOnInit(): void {
    if (!this.clientCode()) {
      console.error('Client code is required');
      return;
    }

    if (this.patientId()) {
      this.store.dispatch(
        sadmActions.getSadmEntities({
          clientCode: this.clientCode(),
          patientId: this.patientId(),
          isArchived: false,
        }),
      );
    }

    this.isDataLoaded$ = this.patientId()
      ? this.store.select(selectIsPatientSadmStateLoaded(this.clientCode(), this.patientId()))
      : of(true);

    if (!this.isDialog()) {
      this.selectedSadmIdControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((sadmEntityId) => {
        if (sadmEntityId === this.DRAFT_PLACEHOLDER_ID) {
          this.selectionChange.emit({ sadmEntityDraft: this.draft() });
          return;
        }
        this.selectionChange.emit({ sadmEntityId });
      });
    }

    if (this.autoselection()) {
      this.autoselectedSadmEntity$.pipe(first()).subscribe((sadmEntity) => {
        if (!sadmEntity && this.draft()) {
          this.selectPlaceholder();
        } else if (sadmEntity) {
          this.selectedSadmIdControl.setValue(sadmEntity.id);
        }
      });
    }
  }

  validate(): void {
    if (this.selectedSadmIdControl.valid) {
      this.context.completeWith({
        sadmEntityId: this.selectedSadmIdControl.value,
      });
    }
  }

  close(): void {
    this.context.completeWith(null);
  }

  createEntity(sadmClient: SadmClient): void {
    this.sadmEntityService
      .openEntityCreation(sadmClient, this.patientId(), this.clientCode(), this.formInputs())
      .pipe(first())
      .subscribe((sadmEntityDraft) => {
        if (!sadmEntityDraft) {
          return;
        }

        if (this.isDialog()) {
          this.context.completeWith({ sadmEntityDraft });
        } else {
          this.entityCreated.emit(sadmEntityDraft);
        }
      });
  }

  selectPlaceholder(): void {
    this.selectedSadmIdControl.setValue(this.DRAFT_PLACEHOLDER_ID);
  }
}
